import { useState, type ReactNode } from "react";
import { ArrowUpRight, Search } from "lucide-react";
import { Badge, Button } from "@/design-system/primitives";
import type { LabObservation, LabResult } from "@/types/labs";
import { labDate } from "./labPresentation";
import {
  labCategories,
  labCategory,
  labCounts,
  labFlag,
  markerHistory,
  type LabRecord,
} from "./labBiomarkers";
import { LabBiomarkerDetail, LabStatus, LabTrend } from "./LabBiomarkerDetail";
import { labReferenceRange } from "./labReferenceRanges";
import { useLabEvent } from "./labExperiment";
export { labFlag } from "./labBiomarkers";

export function LabSummary({
  items,
  compact = false,
}: {
  items: LabObservation[];
  compact?: boolean;
}) {
  const counts = labCounts(items);
  return (
    <div>
      <dl
        className={`grid grid-cols-2 ${compact ? "" : "sm:grid-cols-4"} gap-15`}
      >
        {[
          [counts.total, "Biomarkers"],
          [counts.normal, "Normal (lab)"],
          [counts.flagged, "Flagged by lab"],
          [counts.unspecified, "No lab flag"],
        ].map(([count, label]) => (
          <div key={label}>
            <dd
              className={`font-display ${compact ? "text-24" : "text-32"} tabular-nums leading-110`}
            >
              {count}
            </dd>
            <dt className="type-body-sm text-ink-secondary mt-6">{label}</dt>
          </div>
        ))}
      </dl>
      <div className="flex w-full h-5 gap-3 mt-20 overflow-hidden" aria-hidden>
        {counts.normal > 0 && (
          <span className="bg-success" style={{ flex: counts.normal }} />
        )}
        {counts.flagged > 0 && (
          <span className="bg-warning" style={{ flex: counts.flagged }} />
        )}
        {counts.unspecified > 0 && (
          <span className="bg-ink-muted" style={{ flex: counts.unspecified }} />
        )}
      </div>
    </div>
  );
}
export function LabResultCard({
  result,
  onReport,
  download,
  records = [],
  initialMarker,
  clinical = false,
}: {
  result: LabResult;
  onReport: () => void;
  download?: ReactNode;
  records?: LabRecord[];
  initialMarker?: number;
  clinical?: boolean;
}) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [category, setCategory] = useState("all");
  const [selected, setSelected] = useState<number | null>(
    initialMarker ?? null,
  );
  const emit = useLabEvent();
  const categories = labCategories.filter((group) =>
    result.observations.some((item) => labCategory(item).id === group.id),
  );
  const categoryItems = result.observations.filter(
    (item) => category === "all" || labCategory(item).id === category,
  );
  const counts = labCounts(categoryItems);
  const activeCategory = categories.find((group) => group.id === category);
  const visible = result.observations
    .map((item, index) => ({ item, index }))
    .filter(
      ({ item }) =>
        (category === "all" || labCategory(item).id === category) &&
        (filter === "all" || labFlag(item) === filter) &&
        item.name.toLowerCase().includes(search.trim().toLowerCase()),
    );
  const qualityMessage =
    result.quality_status === "partial"
      ? "This report contains partial results. Additional results may still be needed."
      : result.quality_status === "quality_issue"
        ? "The laboratory reported a sample quality issue. Your care team will review whether another sample is needed."
        : result.quality_status === "unrecognized"
          ? "This report needs further review before its results can be displayed."
          : null;
  const chooseCategory = (id: string) => {
    setCategory(id);
    setSearch("");
    setFilter("all");
    if (!clinical)
      emit("blood_test_category_viewed", { source: "lab_results" });
  };
  return (
    <article className="ph-no-capture border border-line-subtle bg-page rounded-card overflow-hidden">
      <header className="p-20 sm:p-25 border-b border-line-subtle flex flex-wrap items-start justify-between gap-15">
        <div>
          <p className="type-label-xs text-ink-secondary">Your lab report</p>
          <h3 className="font-display text-28 leading-120 mt-8">
            Your results, at a glance.
          </h3>
          <p className="type-body-sm text-ink-secondary mt-10">
            {result.collected_at
              ? `Collected ${labDate(result.collected_at)} · `
              : ""}
            Received {labDate(result.created_at)}
          </p>
        </div>
        <div className="flex flex-wrap items-start gap-10">
          {download}
          <Button
            variant="secondary"
            disabled={!result.report_available}
            onClick={onReport}
          >
            {result.report_available ? "View report" : "Report pending"}
          </Button>
        </div>
      </header>
      {qualityMessage && (
        <div className="m-20 p-15 border-l-2 border-warning bg-warning-wash">
          <Badge tone="warning">Review needed</Badge>
          <p className="type-body-md mt-10 leading-145">{qualityMessage}</p>
        </div>
      )}
      {result.observations.length ? (
        <div className="grid lg:grid-cols-[21rem_minmax(0,1fr)]">
          <nav
            aria-label="Biomarker categories"
            className="border-b lg:border-b-0 lg:border-r border-line-subtle bg-raised p-12 flex lg:flex-col gap-6 overflow-x-auto"
          >
            {[{ id: "all", name: "Summary" }, ...categories].map((group) => (
              <button
                key={group.id}
                type="button"
                aria-current={category === group.id ? "page" : undefined}
                onClick={() => chooseCategory(group.id)}
                className="shrink-0 lg:shrink min-h-44 px-12 py-10 text-left flex items-center justify-between gap-12 text-14 text-ink-secondary border border-transparent aria-[current=page]:bg-page aria-[current=page]:border-line-default aria-[current=page]:text-ink focus-visible:outline-accent hover:bg-page"
              >
                <span>{group.name}</span>
                <span className="font-mono text-11 text-ink-secondary">
                  {group.id === "all"
                    ? result.observations.length
                    : result.observations.filter(
                        (item) => labCategory(item).id === group.id,
                      ).length}
                </span>
              </button>
            ))}
          </nav>
          <div className="min-w-0">
            <section
              className="p-20 sm:p-25 border-b border-line-subtle"
              aria-label={activeCategory?.name ?? "Biomarker summary"}
            >
              <h4 className="font-display text-24">
                {activeCategory?.name ?? "Your biomarkers"}
              </h4>
              <p className="type-body-md text-ink-secondary leading-145 mt-8 mb-25">
                {activeCategory?.description ??
                  "A closer look at the measurements in your blood test."}
              </p>
              <LabSummary items={categoryItems} />
              <p className="type-body-sm text-ink-secondary leading-145 mt-15">
                {counts.flagged
                  ? `${counts.flagged} ${counts.flagged === 1 ? "measurement is" : "measurements are"} flagged by the laboratory.`
                  : "No high, low or abnormal flags were supplied for these measurements."}{" "}
                {counts.unspecified > 0
                  ? "A missing flag does not mean a result is in range."
                  : "Flags reflect the laboratory’s assessment."}
              </p>
            </section>
            <div className="p-15 sm:px-25 border-b border-line-subtle flex flex-wrap items-center gap-10">
              <label className="flex flex-1 items-center min-w-[16rem] gap-8 min-h-44 border border-line-subtle px-12">
                <Search size={15} aria-hidden className="text-ink-secondary" />
                <input
                  aria-label="Search measurements"
                  placeholder="Search measurements"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="w-full min-w-0 bg-transparent text-14 py-10"
                />
              </label>
              <select
                aria-label="Filter by laboratory flag"
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
                className="bg-page border border-line-subtle min-h-44 px-10 text-13 max-w-full"
              >
                <option value="all">All results</option>
                <option value="flagged">
                  Flagged by lab ({counts.flagged})
                </option>
                <option value="normal">Normal (lab)</option>
                <option value="unspecified">No lab flag</option>
              </select>
            </div>
            <div className="sr-only" aria-live="polite">
              {visible.length} measurements shown
            </div>
            <div className="hidden sm:grid grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_8rem_2rem] gap-12 px-25 py-12 bg-raised type-label-xs text-ink-secondary">
              <span>Biomarker</span>
              <span>Lab flag</span>
              <span>Value</span>
              <span>Trend / range</span>
              <span />
            </div>
            {visible.length ? (
              <ul>
                {visible.map(({ item, index }) => {
                  const history = markerHistory(item, result, records);
                  const range = labReferenceRange(item);
                  return (
                    <li
                      key={index}
                      className="border-t first:border-t-0 border-line-subtle"
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setSelected(index);
                          if (!clinical)
                            emit("blood_test_biomarker_viewed", {
                              source: "lab_results",
                            });
                        }}
                        className="group w-full text-left p-20 sm:px-25 grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_8rem_2rem] gap-x-12 gap-y-12 items-center hover:bg-raised focus-visible:outline-accent"
                        aria-label={`View ${item.name} details`}
                      >
                        <div className="min-w-0">
                          <p className="text-11 leading-130 text-ink-secondary mb-5">
                            {labCategory(item).name}
                          </p>
                          <h5 className="font-display text-16 leading-130 break-words">
                            {item.name}
                          </h5>
                        </div>
                        <div className="row-start-2 sm:row-auto">
                          <LabStatus item={item} />
                        </div>
                        <p className="font-display text-20 sm:text-18 tabular-nums break-words col-start-2 row-start-1 sm:col-auto sm:row-auto">
                          {item.value_text || "Not reported"}
                        </p>
                        <div className="hidden sm:block">
                          {history.length > 1 ||
                          (history.length === 1 && range) ? (
                            <LabTrend compact points={history} range={range} />
                          ) : (
                            <span className="text-11 text-ink-secondary">
                              {history.length === 1 ? "First result" : "—"}
                            </span>
                          )}
                        </div>
                        <ArrowUpRight
                          size={16}
                          aria-hidden
                          className="justify-self-end text-ink-secondary row-start-2 col-start-2 sm:row-auto sm:col-auto group-hover:text-ink"
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="p-25 type-body-md">
                {search
                  ? "No measurements match your search."
                  : "No measurements match this filter."}
              </p>
            )}
          </div>
        </div>
      ) : (
        <p className="p-25 type-body-md text-ink-secondary">
          No individual marker values are available for this report. Review the
          original report when available.
        </p>
      )}
      <footer className="px-20 sm:px-25 py-15 border-t border-line-subtle bg-raised text-12 text-ink-secondary leading-145">
        Values and flags are supplied by the laboratory. Select a biomarker for
        its details and available history.{" "}
        {(result.report_count ?? 1) > 1 &&
          `${result.report_count} source documents. Open View report to choose a document.`}
        {result.notes.map((note, index) => (
          <p key={index} className="mt-8">
            {note}
          </p>
        ))}
      </footer>
      <LabBiomarkerDetail
        item={
          selected === null ? null : (result.observations[selected] ?? null)
        }
        result={result}
        records={records}
        onClose={() => setSelected(null)}
        onReport={onReport}
        clinical={clinical}
        download={download}
      />
    </article>
  );
}
